/* Movatic WMS — Frontend | CesarFilho 932048016 */
import { useEffect, useMemo, useState } from 'react'
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query'
import { api, apiDelete } from '@/lib/api'
import DataTable, { Column } from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import DynamicForm from '@/components/DynamicForm'

type ListResponse<T = any> = {
  count: number
  results: T[]
  _error?: string
}

type CrudListProps<T = any> = {
  title: string
  endpoint: string
  columns: Column<T>[]
  searchParam?: string
  writable?: boolean // F2: liga criar/editar/apagar; ausente = read-only (atual)
  canDelete?: boolean // ADR-0039: quando writable, controla o botão Apagar (default true). Cadastros de negócio (Customer) usam false — desativa-se via is_active, nunca apaga (preserva histórico/rastreabilidade).
  rowActions?: (row: T) => React.ReactNode // ADR-0039: botões extra por linha, ao lado de Editar/Apagar (consumidor traz o JSX, ex.: Link Ocorrências). CrudList fica router-agnóstico.
}

export default function CrudList({
  title,
  endpoint,
  columns,
  searchParam = 'search',
  writable = false,
  canDelete = true,
  rowActions,
}: CrudListProps) {
  const [qInput, setQInput] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // F2: um so' estado -> undefined=fechado, null=criar, obj=editar
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<any | null | undefined>(undefined)

  // Debounce simples para a busca
  const q = useDebounce(qInput, 350)

  // Volta para a página 1 ao mudar busca ou tamanho da página
  useEffect(() => {
    setPage(1)
  }, [q, pageSize])

  const { data, isFetching, error } = useQuery<ListResponse>({
    queryKey: ['crud', endpoint, q, page, pageSize, searchParam],
    queryFn: async () => {
      const params: Record<string, any> = { page, page_size: pageSize }
      if (q) params[searchParam] = q

      try {
        const resp = await api.get(endpoint, { params })
        const d = resp.data

        // Normaliza: DRF { count, results } OU um array simples
        if (d && typeof d === 'object' && Array.isArray(d.results)) {
          return { count: d.count ?? d.results.length, results: d.results }
        }
        if (Array.isArray(d)) {
          return { count: d.length, results: d }
        }
        return { count: 0, results: [] }
      } catch (e: any) {
        const msg =
          e?.response?.status === 404
            ? 'Endpoint não encontrado (404). Confira a rota.'
            : e?.message || 'Erro ao carregar dados.'
        return { count: 0, results: [], _error: msg }
      }
    },
    // v5: substitui keepPreviousData
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  const rows: any[] = useMemo(() => data?.results ?? [], [data])
  const total = data?.count ?? rows.length
  const warnMsg = data?._error

  // F2: refresca a lista (invalida todas as paginas/buscas deste endpoint) e fecha o form
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['crud', endpoint] })
    setEditing(undefined)
  }

  const onDelete = async (row: any) => {
    if (!window.confirm('Apagar este registo?')) return
    try {
      await apiDelete(`${endpoint}${row.id}/`)
      refresh()
    } catch (e: any) {
      window.alert(e?.message || 'Erro ao apagar.')
    }
  }

  // F2/rowActions: coluna de acoes aparece com writable OU rowActions (consumidores
  // sem ambos ficam intactos). Editar/Apagar so' quando writable; extras sempre que dados.
  const showActions = writable || !!rowActions
  const cols: Column<any>[] = showActions
    ? [
        ...columns,
        {
          key: '_actions',
          header: '',
          render: (row: any) => (
            <div className="flex gap-2">
              {writable && (
                <button className="btn subtle" onClick={() => setEditing(row)}>
                  Editar
                </button>
              )}
              {writable && canDelete && (
                <button className="btn danger" onClick={() => onDelete(row)}>
                  Apagar
                </button>
              )}
              {rowActions?.(row)}
            </div>
          ),
        } as Column<any>,
      ]
    : columns

  return (
    <div className="grid gap-4">
      {/* Cabeçalho / filtros */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold">{title}</h2>
          <div className="flex items-center gap-3">
            {isFetching && (
              <span className="text-xs text-neutral-500">Atualizando…</span>
            )}
            {writable && (
              <button className="btn" onClick={() => setEditing(null)}>
                Novo
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
          <div className="flex items-center gap-2">
            <input
              className="input w-full"
              placeholder="Buscar..."
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
            {qInput && (
              <button
                className="btn subtle"
                onClick={() => setQInput('')}
                title="Limpar"
              >
                Limpar
              </button>
            )}
          </div>

          <select
            className="input"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 50, 100].map((s) => (
              <option key={s} value={s}>
                {s} por página
              </option>
            ))}
          </select>
        </div>

        {warnMsg && (
          <div className="mt-2 text-sm text-red-600">Aviso: {warnMsg}</div>
        )}
        {error && !data && (
          <div className="mt-2 text-sm text-red-600">Erro ao consultar.</div>
        )}
      </div>

      {/* Lista */}
      <div className="card p-4">
        <DataTable columns={cols} data={rows} loading={!!isFetching} />

        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-neutral-500">
            <span>
              Total: <b>{total}</b> itens
            </span>
          </div>

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* F2: formulario dirigido por schema (criar/editar) */}
      {writable && editing !== undefined && (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setEditing(undefined)
          }}
        >
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editing ? 'Editar' : 'Novo'} — {title}
              </DialogTitle>
            </DialogHeader>
            <DynamicForm
              endpoint={endpoint}
              initial={editing}
              onDone={refresh}
              onCancel={() => setEditing(undefined)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/** Debounce simples para valores controlados */
function useDebounce<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}
